/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║  DANGER ZONE OVERLAY - Visual shifts based on game state                  ║
 * ║  Changes entire UI atmosphere during critical moments                     ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 * 
 * Visual states:
 * - Safe (>30 min): Normal cyberpunk green/cyan
 * - Warning (30-5 min): Orange tint creeps in
 * - Danger (<5 min): Red pulse, screen edges glow
 * - Critical (<60s): Full red, heartbeat pulse, screen shake
 * - Blood Moon: Deep crimson wash over everything
 * - Chaos Event: Purple/magenta lightning flashes
 * - Extinction: White flash + screen shake
 */

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import BodyPortal from './BodyPortal';

interface DangerZoneProps {
  timeRemaining: number;
  isBloodMoon?: boolean;
  chaosActive?: boolean;
  extinctionActive?: boolean;
  flashCrashActive?: boolean;
  fomoSurgeActive?: boolean;
  assassinActive?: boolean;
  carnivalActive?: boolean;
}

type GameAtmosphere = 'normal' | 'warning' | 'danger' | 'critical' | 'blood_moon' | 'chaos' | 'extinction' | 'flash_crash' | 'fomo_surge' | 'assassin' | 'carnival';

function getAtmosphere(props: DangerZoneProps): GameAtmosphere {
  // Priority: extinction > blood_moon > carnival > assassin > fomo_surge > flash_crash > chaos > critical > danger > warning > normal
  if (props.extinctionActive) return 'extinction';
  if (props.isBloodMoon) return 'blood_moon';
  if (props.carnivalActive) return 'carnival';
  if (props.assassinActive) return 'assassin';
  if (props.fomoSurgeActive) return 'fomo_surge';
  if (props.flashCrashActive) return 'flash_crash';
  if (props.chaosActive) return 'chaos';
  if (props.timeRemaining >= 0 && props.timeRemaining < 60) return 'critical';
  if (props.timeRemaining >= 0 && props.timeRemaining < 300) return 'danger';
  if (props.timeRemaining >= 0 && props.timeRemaining < 1800) return 'warning';
  return 'normal';
}

const ATMOSPHERE_CONFIG: Record<GameAtmosphere, {
  borderColor: string;
  borderOpacity: number;
  pulseColor: string;
  vignetteColor: string;
  vignetteOpacity: number;
  screenShake: boolean;
  scanlineColor: string;
  label?: string;
  labelColor?: string;
  pulseSpeed?: number; // seconds per cycle
  innerGlow?: string;  // extra inner box-shadow layer
}> = {
  normal: {
    borderColor: 'transparent',
    borderOpacity: 0,
    pulseColor: 'transparent',
    vignetteColor: 'transparent',
    vignetteOpacity: 0,
    screenShake: false,
    scanlineColor: 'rgba(0,0,0,0.03)',
  },
  warning: {
    borderColor: 'rgba(255,140,50,0.35)',
    borderOpacity: 0.35,
    pulseColor: 'rgba(255,140,50,0.06)',
    vignetteColor: 'rgba(255,100,0,0.18)',
    vignetteOpacity: 0.18,
    screenShake: false,
    scanlineColor: 'rgba(255,100,0,0.02)',
    pulseSpeed: 3,
  },
  danger: {
    borderColor: 'rgba(255,50,0,0.6)',
    borderOpacity: 0.6,
    pulseColor: 'rgba(255,50,0,0.1)',
    vignetteColor: 'rgba(255,0,0,0.3)',
    vignetteOpacity: 0.3,
    screenShake: false,
    scanlineColor: 'rgba(255,0,0,0.04)',
    label: '⚠ DANGER ZONE',
    labelColor: '#ff3300',
    pulseSpeed: 2,
    innerGlow: 'inset 0 0 40px rgba(255,50,0,0.08)',
  },
  critical: {
    borderColor: 'rgba(255,0,0,0.8)',
    borderOpacity: 0.8,
    pulseColor: 'rgba(255,0,0,0.15)',
    vignetteColor: 'rgba(255,0,0,0.5)',
    vignetteOpacity: 0.5,
    screenShake: true,
    scanlineColor: 'rgba(255,0,0,0.06)',
    label: '💀 FINAL SECONDS',
    labelColor: '#ff0000',
    pulseSpeed: 0.6,
    innerGlow: 'inset 0 0 100px rgba(255,0,0,0.15)',
  },
  blood_moon: {
    borderColor: 'rgba(180,0,0,0.7)',
    borderOpacity: 0.7,
    pulseColor: 'rgba(180,0,0,0.12)',
    vignetteColor: 'rgba(120,0,0,0.45)',
    vignetteOpacity: 0.45,
    screenShake: false,
    scanlineColor: 'rgba(180,0,0,0.05)',
    label: '🌑 BLOOD MOON',
    labelColor: '#cc0000',
    pulseSpeed: 3,
    innerGlow: 'inset 0 0 120px rgba(120,0,0,0.1)',
  },
  chaos: {
    borderColor: 'rgba(153,50,204,0.65)',
    borderOpacity: 0.65,
    pulseColor: 'rgba(153,50,204,0.12)',
    vignetteColor: 'rgba(153,50,204,0.3)',
    vignetteOpacity: 0.3,
    screenShake: false,
    scanlineColor: 'rgba(153,50,204,0.04)',
    label: '⚡ CHAOS EVENT',
    labelColor: '#9932cc',
    pulseSpeed: 1.5,
    innerGlow: 'inset 0 0 60px rgba(153,50,204,0.1)',
  },
  extinction: {
    borderColor: 'rgba(255,255,255,0.9)',
    borderOpacity: 0.9,
    pulseColor: 'rgba(255,255,255,0.2)',
    vignetteColor: 'rgba(255,100,0,0.5)',
    vignetteOpacity: 0.5,
    screenShake: true,
    scanlineColor: 'rgba(255,200,0,0.06)',
    label: '☄ EXTINCTION EVENT',
    labelColor: '#ff6600',
    pulseSpeed: 0.5,
    innerGlow: 'inset 0 0 150px rgba(255,100,0,0.15)',
  },
  flash_crash: {
    borderColor: 'rgba(0,255,136,0.55)',
    borderOpacity: 0.55,
    pulseColor: 'rgba(0,255,136,0.1)',
    vignetteColor: 'rgba(0,255,136,0.25)',
    vignetteOpacity: 0.25,
    screenShake: false,
    scanlineColor: 'rgba(0,255,136,0.04)',
    label: '📉 FLASH CRASH',
    labelColor: '#00ff88',
    pulseSpeed: 1,
    innerGlow: 'inset 0 0 60px rgba(0,255,136,0.08)',
  },
  fomo_surge: {
    borderColor: 'rgba(255,0,128,0.65)',
    borderOpacity: 0.65,
    pulseColor: 'rgba(255,0,128,0.12)',
    vignetteColor: 'rgba(255,0,128,0.3)',
    vignetteOpacity: 0.3,
    screenShake: false,
    scanlineColor: 'rgba(255,0,128,0.04)',
    label: '🚀 FOMO SURGE',
    labelColor: '#ff0080',
    pulseSpeed: 1.2,
    innerGlow: 'inset 0 0 80px rgba(255,0,128,0.1)',
  },
  assassin: {
    borderColor: 'rgba(220,20,60,0.7)',
    borderOpacity: 0.7,
    pulseColor: 'rgba(220,20,60,0.12)',
    vignetteColor: 'rgba(100,0,0,0.5)',
    vignetteOpacity: 0.5,
    screenShake: false,
    scanlineColor: 'rgba(220,20,60,0.05)',
    label: '🗡 ASSASSIN MODE',
    labelColor: '#dc143c',
    pulseSpeed: 1,
    innerGlow: 'inset 0 0 100px rgba(100,0,0,0.2)',
  },
  carnival: {
    borderColor: 'rgba(255,215,0,0.6)',
    borderOpacity: 0.6,
    pulseColor: 'rgba(255,0,255,0.1)',
    vignetteColor: 'rgba(255,100,200,0.25)',
    vignetteOpacity: 0.25,
    screenShake: false,
    scanlineColor: 'rgba(255,0,255,0.04)',
    label: '🎪 CHAOS CARNIVAL',
    labelColor: '#ffd700',
    pulseSpeed: 0.8,
    innerGlow: 'inset 0 0 80px rgba(255,0,255,0.1)',
  },
};

/** High-contrast accent per event, tuned for the navy holo HUD (text stays light; accent drives border/dot/glow). */
const EVENT_ACCENT: Record<string, string> = {
  warning: '#ffd23f',
  danger: '#ff9f43',
  critical: '#ff4757',
  blood_moon: '#ff4d6d',
  chaos: '#b57bff',
  extinction: '#ff6b6b',
  flash_crash: '#46def8',
  fomo_surge: '#3dffa8',
  assassin: '#f058f6',
  carnival: '#ffb86b',
};

export default function DangerZone(props: DangerZoneProps) {
  const atmosphere = getAtmosphere(props);
  const config = ATMOSPHERE_CONFIG[atmosphere];
  // Event chips render into the dedicated slot under the header (#holo-event-slot), never floating over it.
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    let raf = 0;
    const find = () => {
      const el = document.getElementById('holo-event-slot');
      if (el) setSlot(el); else raf = requestAnimationFrame(find);
    };
    find();
    return () => cancelAnimationFrame(raf);
  }, []);
  const [flashVisible, setFlashVisible] = useState(false);

  // Flash effect on extinction
  useEffect(() => {
    if (props.extinctionActive) {
      setFlashVisible(true);
      const timer = setTimeout(() => setFlashVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [props.extinctionActive]);

  if (atmosphere === 'normal') return null;

  return ( <BodyPortal>
    <>
      {/* Screen edge vignette (none for Blood Moon: accent swap only) */}
      {atmosphere !== 'blood_moon' && <div
        className="fixed inset-0 pointer-events-none z-30 transition-opacity duration-1000"
        style={{
          background: `radial-gradient(ellipse 80% 80% at 50% 50%, transparent 50%, ${config.vignetteColor} 100%)`,
          opacity: config.vignetteOpacity,
        }}
      />}

      {/* Pulsing border glow */}
      {atmosphere !== 'blood_moon' && <motion.div
        className="fixed inset-0 pointer-events-none z-30"
        style={{
          boxShadow: `inset 0 0 80px ${config.borderColor}, inset 0 0 160px ${config.pulseColor}${config.innerGlow ? ', ' + config.innerGlow : ''}`,
        }}
        animate={{
          opacity: [config.borderOpacity, config.borderOpacity * 0.4, config.borderOpacity],
        }}
        transition={{
          duration: config.pulseSpeed ?? (atmosphere === 'critical' ? 0.8 : 2),
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />}

      {/* Screen shake for critical moments */}
      {config.screenShake && (
        <style>{`
          @keyframes screenShake {
            0%, 100% { transform: translate(0, 0); }
            10% { transform: translate(-1px, 0); }
            20% { transform: translate(1px, -1px); }
            30% { transform: translate(-1px, 1px); }
            40% { transform: translate(1px, 0); }
            50% { transform: translate(-1px, -1px); }
            60% { transform: translate(1px, 1px); }
            70% { transform: translate(0, -1px); }
            80% { transform: translate(-1px, 0); }
            90% { transform: translate(1px, 1px); }
          }
          .screen-shake { animation: screenShake 0.3s ease-in-out infinite; }
        `}</style>
      )}

      {/* White flash on extinction */}
      <AnimatePresence>
        {flashVisible && (
          <motion.div
            className="fixed inset-0 pointer-events-none z-50 bg-white"
            initial={{ opacity: 0.8 }}
            animate={{ opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          />
        )}
      </AnimatePresence>

      {/* Status label: a chip in the event slot under the header (falls back to bottom-left if the slot is absent) */}
      {config.label && (() => {
        const chip = (
          <span
            className={`holo-event-chip ev-${atmosphere}`}
            style={{ ['--ev' as any]: EVENT_ACCENT[atmosphere] ?? config.labelColor }}
            role="status"
          >
            <i className="dot" aria-hidden="true" />
            {config.label.replace(/^[^A-Za-z0-9]+\s*/, '')}
          </span>
        );
        return slot ? createPortal(chip, slot) : <div className="holo-event-fallback">{chip}</div>;
      })()}
    </>
  </BodyPortal>);
}

/**
 * Hook to apply danger zone CSS class to body element
 * for global style overrides during critical moments
 */
export function useDangerZoneClass(timeRemaining: number, isBloodMoon?: boolean, assassinActive?: boolean, carnivalActive?: boolean) {
  useEffect(() => {
    const body = document.body;
    // Remove all previous atmosphere classes
    body.classList.remove('atmosphere-warning', 'atmosphere-danger', 'atmosphere-critical', 'atmosphere-blood-moon', 'atmosphere-assassin', 'atmosphere-carnival');
    
    if (isBloodMoon) {
      body.classList.add('atmosphere-blood-moon');
    } else if (carnivalActive) {
      body.classList.add('atmosphere-carnival');
    } else if (assassinActive) {
      body.classList.add('atmosphere-assassin');
    } else if (timeRemaining >= 0 && timeRemaining < 60) {
      body.classList.add('atmosphere-critical');
    } else if (timeRemaining >= 0 && timeRemaining < 300) {
      body.classList.add('atmosphere-danger');
    } else if (timeRemaining >= 0 && timeRemaining < 1800) {
      body.classList.add('atmosphere-warning');
    }

    return () => {
      body.classList.remove('atmosphere-warning', 'atmosphere-danger', 'atmosphere-critical', 'atmosphere-blood-moon', 'atmosphere-assassin', 'atmosphere-carnival');
    };
  }, [timeRemaining, isBloodMoon, assassinActive, carnivalActive]);
}
